'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';

export default function Footer() {
  const { user, signOut } = useAuth();
  const router = useRouter();

  const handleLogout = async () => {
    try {
      await signOut();
      router?.push('/login');
    } catch {
      // ignore
    }
  };

  return (
    <footer className="border-t border-border-subtle py-8">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Pattern 8: GitHub Developer Minimal — single row */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Left: Logo + copyright */}
          <div className="flex items-center gap-4">
            <AppLogo size={24} text="Compassion Check-in" className="text-silver-dim" />
            <span className="font-mono text-[11px] text-silver-dim opacity-50">
              © 2026 Compassion Check-in Ministry Technologies, Inc.
            </span>
          </div>

          {/* Center: Essential links */}
          <div className="flex items-center gap-6">
            {['Privacy', 'Terms', 'Security', 'Status', 'Docs']?.map((link) => (
              <a
                key={link}
                href="#"
                className="font-mono text-[11px] tracking-wider text-silver-dim opacity-50 hover:opacity-100 hover:text-gold transition-all duration-200"
              >
                {link}
              </a>
            ))}
          </div>

          {/* Right: Social + Logout */}
          <div className="flex items-center gap-3">
            {/* Twitter/X */}
            <a
              href="#"
              aria-label="Twitter"
              className="w-8 h-8 border border-border-subtle flex items-center justify-center text-silver-dim hover:text-gold hover:border-gold transition-all duration-200"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6c2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4c-.9-4.2 4-6.6 7-3.8c1.1 0 3-1.2 3-1.2" />
              </svg>
            </a>
            {/* LinkedIn */}
            <a
              href="#"
              aria-label="LinkedIn"
              className="w-8 h-8 border border-border-subtle flex items-center justify-center text-silver-dim hover:text-gold hover:border-gold transition-all duration-200"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                <rect x="2" y="9" width="4" height="12" />
                <circle cx="4" cy="4" r="2" />
              </svg>
            </a>

            {/* Logout button — only shown when authenticated */}
            {user && (
              <button
                onClick={handleLogout}
                aria-label="Sign out"
                className="flex items-center gap-1.5 px-3 py-1.5 border border-border-subtle font-mono text-[11px] tracking-wider text-silver-dim opacity-60 hover:opacity-100 hover:text-red-400 hover:border-red-400 transition-all duration-200"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Sign Out
              </button>
            )}
          </div>
        </div>

        {/* HIPAA note */}
        <div className="mt-6 pt-6 border-t border-border-subtle text-center">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-silver-dim opacity-30">
            HIPAA Compliant · SOC 2 Type II · Data encrypted at rest and in transit
          </p>
        </div>
      </div>
    </footer>
  );
}